import { useQuery } from '@tanstack/react-query';
import {
  AdvancedFilterPanel,
  type FilterField,
  type FilterOption,
} from '@/components/common/AdvancedFilter';
import { transportTypeQueryOptions } from '@/features/billing/api/billing.api';
import {
  getApprovalStatusFilterField,
  getWorkTypeFilterField,
} from '@/features/billing/utils/billingFilterFields';
import { activeInstituteTypesQueryOptions } from '@/features/instituteTypes/api/instituteTypes.api';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import {
  getDateFromToFilterFields,
  getFiscalYearFilterField,
  getInstituteTypeFilterField,
  getZonalManagerFilterField,
} from '@/utils/filterFields';
import { toFilterOptions } from '@/utils/filters';
import { fiscalYearListQueryOptions } from '../api/fiscalYear.api';
import type { useDcrApproveFilters } from '../hooks/useDcrApproveFilters';

const VERIFY_OPTIONS = [
  { id: 0, name: 'Not verified' },
  { id: 1, name: 'Verified' },
  { id: 2, name: 'Rejected' },
] as const;

const YES_NO_OPTIONS: FilterOption[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

interface DcrApproveFilterPanelProps {
  controller: ReturnType<typeof useDcrApproveFilters>;
}

export function DcrApproveFilterPanel({ controller }: DcrApproveFilterPanelProps) {
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());
  const { data: transportTypes } = useQuery(transportTypeQueryOptions());
  const { data: fiscalYears } = useQuery(fiscalYearListQueryOptions());
  const { data: instituteTypes } = useQuery(activeInstituteTypesQueryOptions());

  const transportOptions = toFilterOptions(transportTypes);

  const fields: FilterField[] = [
    ...getDateFromToFilterFields(),
    { key: 'dcrNo', label: 'DCR No', type: 'number', placeholder: 'Any DCR' },
    getZonalManagerFilterField(zonalManagers),
    getWorkTypeFilterField(),
    getFiscalYearFilterField(fiscalYears),
    getApprovalStatusFilterField({ label: 'Approval Status' }),
    {
      key: 'isVerify',
      label: 'Verify Status',
      type: 'select',
      placeholder: 'All Verify States',
      options: toFilterOptions(VERIFY_OPTIONS),
    },
    getInstituteTypeFilterField(instituteTypes),
    {
      key: 'transportTypeId',
      label: 'Transport Type',
      type: 'select',
      placeholder: 'All Transport Types',
      options: transportOptions,
    },
    {
      key: 'approveTransportTypeId',
      label: 'Approved Transport',
      type: 'select',
      placeholder: 'All Transport Types',
      options: transportOptions,
    },
    {
      key: 'isManualEntry',
      label: 'Manual Entry',
      type: 'select',
      placeholder: 'Any',
      options: YES_NO_OPTIONS,
    },
    {
      key: 'isRefZm',
      label: 'Ref ZM',
      type: 'select',
      placeholder: 'Any',
      options: YES_NO_OPTIONS,
    },
  ];

  return <AdvancedFilterPanel fields={fields} {...getFilterPanelProps(controller)} />;
}
